import type { Params, Route } from "@tanstack/react-location"
import { Navigate, Outlet } from "@tanstack/react-location"
import { DiplomaModal } from "./components/DiplomaModal"
import { JourneyNavigationMobileConnected } from "./components/JourneyNavigationMobile"
import { JourneysModal } from "./components/JourneysModal"
import { ProductModal } from "./components/ProductModal"
import { VppModalLoader } from "./components/ProductModal/VppModal/VppModalLoader"
import { MessagesViewConnected } from "./chat/components/MessagesView"
import { RedirectView } from "./components/RedirectView"
import { ViewerModal } from "./components/ViewerModal"
import { KpiSalesModalLoader } from "./components/WidgetLayout/KpiSalesModal/KpiSalesModalLoader"
import { WidgetLayoutLoader } from "./components/WidgetLayout/WidgetLayoutLoader"
import type { Jid } from "./chat/xmpp/jid"
import type { LocationGenerics } from "./tools/useRouteMetadata"
import { EventHeader, EventsView } from "./views/EventsView"
import { ExploreView } from "./views/ExploreView"
import { ForYouView } from "./views/ForYouView"
import { RecentActivityWidgetModal } from "./views/ForYouView/RecentActivityWidgetModal"
import { HomeViewConnected } from "./views/HomeView"
import { JourneyViewConnected } from "./views/JourneyView"
import { LeaderboardViewResponsive } from "./views/LeaderboardView/LeaderboardViewResponsive"
import { NewsPostViewConnected } from "./views/NewsPostView"
import { NewsViewConnected } from "./views/NewsView/"
import { ProductBundleView } from "./views/ProductBundleView"
import { BundleHeaderConnected } from "./views/ProductBundleView/BundleHeader"
import { SingleJourneyViewConnected } from "./views/SingleJourneyView"
import { UserProfileView } from "./views/UserProfileView"

// TODO https://jira.attensi.com/browse/WEB-19655
// Refactor routes to be more maintanable and easier to read,
// and to allow for customizing layout.
export const getRoutes = (): Route<LocationGenerics>[] => [
  {
    path: "/",
    element: (
      <WidgetLayoutLoader>
        <HomeViewConnected />
      </WidgetLayoutLoader>
    ),
    meta: { isReturnablePage: true },
  },

  {
    path: "/for-you",
    element: (
      <WidgetLayoutLoader showMandatoryAndIncompleteFilters={true}>
        <ForYouView />
        <Outlet />
      </WidgetLayoutLoader>
    ),
    children: getChildRoutes(() => "/for-you"),
    meta: { isReturnablePage: true },
  },

  {
    path: "/journey",
    element: (
      <>
        <JourneyNavigationMobileConnected />
        <WidgetLayoutLoader
          showUserInfoHeader={false}
          showJourneyNavigation={true}
        >
          <JourneyViewConnected />
          <Outlet />
        </WidgetLayoutLoader>
      </>
    ),

    children: [
      {
        path: "/list",
        element: async () => <JourneysModal closeRoute="/journey" />,
      },
      ...getChildRoutes(() => "/journey"),
    ],
    meta: { isReturnablePage: true },
  },

  {
    path: "/explore",
    element: (
      <WidgetLayoutLoader>
        <ExploreView />
        <Outlet />
      </WidgetLayoutLoader>
    ),
    children: getChildRoutes(() => "/explore"),
    meta: { isReturnablePage: true },
  },

  {
    path: "/leaderboards",
    element: (
      <WidgetLayoutLoader showUserInfoHeader={false}>
        <LeaderboardViewResponsive />
        <Outlet />
      </WidgetLayoutLoader>
    ),
    children: getChildRoutes(() => "/leaderboards"),
    meta: { isReturnablePage: true },
  },

  {
    path: "/messages",
    // No WidgetLayout: it reserves a 41rem widget column and its user card,
    // and the design gives the whole width to the conversation.
    element: <Outlet />,
    children: [
      {
        path: "/",
        element: <MessagesViewConnected />,
        meta: { isReturnablePage: true },
      },
      {
        // The peer is a bare JID, so its `@` arrives percent-encoded.
        path: "/:peer",
        element: async ({ params }) => (
          <>
            <MessagesViewConnected
              peer={decodeURIComponent(params.peer ?? "") as Jid}
            />
            <Outlet />
          </>
        ),
        // A shared learning item opens its product modal over the open
        // conversation, closing back to it (FAME-810).
        children: getChildRoutes((params) => `/messages/${params.peer}`),
        meta: { isReturnablePage: true },
      },
    ],
  },

  {
    path: "/news/post/:newsPostId",
    element: async ({ params }) => (
      <NewsPostViewConnected id={params.newsPostId!} />
    ),
    meta: { isReturnablePage: true },
  },

  {
    path: "/news",
    element: (
      <WidgetLayoutLoader showUserInfoHeader={false}>
        <NewsViewConnected />
        <Outlet />
      </WidgetLayoutLoader>
    ),
    children: getChildRoutes(() => "/news"),
    meta: { isReturnablePage: true },
  },

  {
    path: "/events",
    element: (
      <>
        <EventHeader />
        <WidgetLayoutLoader showUserInfoHeader={false}>
          <EventsView />
          <Outlet />
        </WidgetLayoutLoader>
      </>
    ),
    children: getChildRoutes(() => `/events`),
    meta: { isReturnablePage: true },
  },

  {
    path: "/bundle/:bundleId",
    element: async ({ params }) => (
      <>
        <BundleHeaderConnected id={params.bundleId!} />
        <WidgetLayoutLoader showUserInfoHeader={false}>
          <ProductBundleView id={params.bundleId!} />
          <Outlet />
        </WidgetLayoutLoader>
      </>
    ),
    children: getChildRoutes((params) => `/bundle/${params.bundleId}`),
    meta: { isReturnablePage: true },
  },

  {
    path: "/me",
    element: (
      <>
        <UserProfileView />
        <Outlet />
      </>
    ),
    children: getChildRoutes(() => "/me"),
    meta: { isReturnablePage: true },
  },

  // https://jira.attensi.com/browse/WEB-19117
  // A special route for a single journey with no additional elements.
  // Those journeys are supposed to be used as separate SCORM packages in
  // external LMSes.
  {
    path: "/_embed/journey/:journeyId",
    element: async ({ params }) => (
      <>
        <SingleJourneyViewConnected journeyId={params.journeyId!} />
        <Outlet />
      </>
    ),
    children: getChildRoutes((params) => `/_embed/journey/${params.journeyId}`),
    meta: { shouldHideUI: true },
  },

  {
    path: "/redirect",
    element: <RedirectView />,
  },

  {
    element: <Navigate to="/" />,
  },
]

const getChildRoutes = (
  getCloseRoute: (params: Params<string>) => string
): Route<LocationGenerics>[] => [
  {
    path: "/diploma/:productId",
    element: async ({ params }) => (
      <DiplomaModal
        closeRoute={getCloseRoute(params)}
        productId={params.productId!}
      />
    ),
  },

  {
    path: "/product/:productId/diploma",
    element: async ({ params }) => (
      <DiplomaModal
        closeRoute={getCloseRoute(params)}
        productId={params.productId!}
      />
    ),
  },

  {
    path: "/product/:productId/play",
    element: async ({ params }) => (
      <ViewerModal
        productId={params.productId!}
        closeRoute={getCloseRoute(params)}
      />
    ),
  },

  {
    path: "/product/:productId",
    element: async ({ params }) => (
      <ProductModal
        closeRoute={getCloseRoute(params)}
        sideContent="none"
        productId={params.productId!}
      />
    ),
  },

  {
    path: "/sim/:productId/vpp",
    element: async ({ params }) => (
      <VppModalLoader
        productId={params.productId!}
        closeRoute={`${getCloseRoute(params)}/sim/${params.productId}`}
      />
    ),
  },

  {
    path: "/sim/:productId/diploma",
    element: async ({ params }) => (
      <DiplomaModal
        closeRoute={getCloseRoute(params)}
        productId={params.productId!}
      />
    ),
  },

  {
    path: "/sim/:productId/play",
    element: async ({ params }) => (
      <ViewerModal
        productId={params.productId!}
        closeRoute={getCloseRoute(params)}
      />
    ),
  },

  {
    path: "/sim/:productId",
    element: async ({ params }) => (
      <ProductModal
        closeRoute={getCloseRoute(params)}
        sideContent="leaderboard"
        productId={params.productId!}
      />
    ),
  },

  {
    path: "/event/:productId/diploma",
    element: async ({ params }) => (
      <DiplomaModal
        closeRoute={getCloseRoute(params)}
        productId={params.productId!}
      />
    ),
  },

  {
    path: "/event/:productId",
    element: async ({ params }) => (
      <ProductModal
        closeRoute={getCloseRoute(params)}
        sideContent="event"
        productId={params.productId!}
      />
    ),
  },

  {
    path: "/activity/:productId/diploma",
    element: async ({ params }) => (
      <DiplomaModal
        closeRoute={getCloseRoute(params)}
        productId={params.productId!}
      />
    ),
  },

  {
    path: "/activity/:productId",
    element: async ({ params }) => (
      <ProductModal
        closeRoute={getCloseRoute(params)}
        sideContent="activity"
        productId={params.productId!}
      />
    ),
  },

  {
    path: "/kpi-sales",
    element: async ({ params }) => (
      <KpiSalesModalLoader closeRoute={getCloseRoute(params)} />
    ),
  },

  {
    path: "/recent-activity-widget",
    element: async ({ params }) => (
      <RecentActivityWidgetModal closeRoute={getCloseRoute(params)} />
    ),
  },
]
